import { GlossaryHelperTypes } from './types'

/**
 * 术语表处理器工具函数
 */

/**
 * 生成占位符标记
 * @param termId 术语ID
 * @param prefix 占位符前缀，默认为 't'
 * @returns 占位符字符串，如 '<t1></t1>'
 */
export function generatePlaceholder(termId: number, prefix: string = 't'): string {
  return `<${prefix}${termId}></${prefix}${termId}>`
}

/**
 * 解析占位符标记，提取索引
 * @param placeholder 占位符字符串
 * @param prefix 占位符前缀，默认为 't'
 * @returns 解析出的索引，如果解析失败返回 null
 */
export function parsePlaceholder(placeholder: string, prefix: string = 't'): number | null {
  const regex = new RegExp(`<${prefix}(\\d+)></${prefix}>`)
  const match = placeholder.match(regex)
  return match ? parseInt(match[1], 10) : null
}

/**
 * 验证术语数据的有效性
 * @param glossaryData 术语表数据
 * @returns 验证结果和错误信息
 */
export function validateGlossaryData(glossaryData: GlossaryHelperTypes.GlossaryData): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!glossaryData.fromLang || typeof glossaryData.fromLang !== 'string') {
    errors.push('无效的源语言代码')
  }

  if (!glossaryData.toLang || typeof glossaryData.toLang !== 'string') {
    errors.push('无效的目标语言代码')
  }

  if (!glossaryData.fromText || typeof glossaryData.fromText !== 'string') {
    errors.push('无效的源文本')
  }

  if (!glossaryData.toText || typeof glossaryData.toText !== 'string') {
    errors.push('无效的目标文本')
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * 创建正则表达式用于术语匹配
 * @param term 术语
 * @param options 匹配选项
 * @returns 正则表达式
 */
export function createTermRegex(
  term: string,
  options: GlossaryHelperTypes.MatchConfig = {}
): RegExp {
  const { caseSensitive = true, wholeWord = true } = options

  // 转义正则表达式特殊字符
  const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // 构建正则表达式模式
  let pattern = escapedTerm

  if (wholeWord) {
    pattern = `\\b${pattern}\\b`
  }

  const flags = caseSensitive ? 'g' : 'gi'
  return new RegExp(pattern, flags)
}

/**
 * 检测术语冲突（重叠或嵌套的术语）
 * @param terms 术语列表
 * @param text 待检测文本
 * @returns 冲突检测结果
 */
export function detectTermConflicts(
  terms: GlossaryHelperTypes.GlossaryData[],
  text: string
): {
  hasConflicts: boolean
  conflicts: Array<{
    term1: string
    term2: string
    position: number
    message: string
  }>
} {
  const conflicts: Array<{
    term1: string
    term2: string
    position: number
    message: string
  }> = []

  for (let i = 0; i < terms.length; i++) {
    for (let j = i + 1; j < terms.length; j++) {
      const term1 = terms[i]
      const term2 = terms[j]

      // 检查是否有术语包含另一个术语
      if (term1.fromText.includes(term2.fromText) || term2.fromText.includes(term1.fromText)) {
        const position = text.indexOf(
          term1.fromText.includes(term2.fromText) ? term1.fromText : term2.fromText
        )
        if (position !== -1) {
          conflicts.push({
            term1: term1.fromText,
            term2: term2.fromText,
            position,
            message: `术语 "${term1.fromText}" 和 "${term2.fromText}" 存在包含关系`,
          })
        }
      }
    }
  }

  return {
    hasConflicts: conflicts.length > 0,
    conflicts,
  }
}

/**
 * 清理和标准化术语文本
 * @param text 原始文本
 * @returns 清理后的文本
 */
export function normalizeTermText(text: string): string {
  return text.trim().replace(/\s+/g, ' ')
}

/**
 * 获取文本中所有占位符的位置信息
 * @param text 包含占位符的文本
 * @param prefix 占位符前缀
 * @returns 占位符位置信息数组
 */
export function findAllPlaceholders(
  text: string,
  prefix = 't'
): Array<{ placeholder: string; start: number; end: number; termId: number }> {
  const regexPattern = `<${prefix}(\\d+)></${prefix}\\1>`
  const regex = new RegExp(regexPattern, 'g')

  const placeholders: Array<{ placeholder: string; start: number; end: number; termId: number }> =
    []

  let match
  while ((match = regex.exec(text)) !== null) {
    const termId = parseInt(match[1], 10)
    const placeholder = match[0]
    const start = match.index
    const end = start + placeholder.length

    placeholders.push({
      placeholder,
      start,
      end,
      termId,
    })
  }
  return placeholders
}

/**
 * 批量替换文本中的占位符
 * @param text 原始文本
 * @param replacements 替换映射
 * @returns 替换后的文本
 */
export function batchReplacePlaceholders(
  text: string,
  replacements: Array<{ placeholder: string; targetTerm: string }>
): string {
  let result = text

  // 按占位符长度降序排列，避免替换时的位置偏移问题
  const sortedReplacements = [...replacements].sort(
    (a, b) => b.placeholder.length - a.placeholder.length
  )

  for (const { placeholder, targetTerm } of sortedReplacements) {
    result = result.replace(
      new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      targetTerm
    )
  }

  return result
}

/**
 * 检测替换项之间的位置重叠（优化版本，使用扫描线算法）
 * @param replacements 替换项数组
 * @returns 重叠检测结果
 */
export function detectPositionOverlaps(
  replacements: Array<{
    start: number
    end: number
    originalText: string
    termIndex: number
    termId: number
  }>
): Array<{
  index1: number
  index2: number
  overlapStart: number
  overlapEnd: number
  message: string
}> {
  if (replacements.length <= 1) {
    return []
  }

  const overlaps: Array<{
    index1: number
    index2: number
    overlapStart: number
    overlapEnd: number
    message: string
  }> = []

  // 优化：对于小规模数据（< 100），使用简单的 O(n²) 算法更高效
  // 对于大规模数据，使用扫描线算法 O(n log n)
  if (replacements.length < 100) {
    // 简单算法：直接比较所有对
    for (let i = 0; i < replacements.length; i++) {
      for (let j = i + 1; j < replacements.length; j++) {
        const r1 = replacements[i]
        const r2 = replacements[j]

        // 检查位置是否重叠
        // 重叠条件：r1.start < r2.end && r2.start < r1.end
        if (r1.start < r2.end && r2.start < r1.end) {
          const overlapStart = Math.max(r1.start, r2.start)
          const overlapEnd = Math.min(r1.end, r2.end)
          overlaps.push({
            index1: i,
            index2: j,
            overlapStart,
            overlapEnd,
            message: `术语 "${r1.originalText}" (位置 ${r1.start}-${r1.end}) 和 "${r2.originalText}" (位置 ${r2.start}-${r2.end}) 在位置 ${overlapStart}-${overlapEnd} 重叠`,
          })
        }
      }
    }
    return overlaps
  }

  // 扫描线算法：O(n log n)
  // 创建事件点：开始和结束位置
  type Event = {
    position: number
    type: 'start' | 'end'
    index: number
  }

  const events: Event[] = []
  for (let i = 0; i < replacements.length; i++) {
    events.push({ position: replacements[i].start, type: 'start', index: i })
    events.push({ position: replacements[i].end, type: 'end', index: i })
  }

  // 按位置排序，相同位置时结束事件优先（避免边界重叠被误判）
  events.sort((a, b) => {
    if (a.position !== b.position) {
      return a.position - b.position
    }
    // 相同位置时，结束事件优先
    if (a.type === 'end' && b.type === 'start') return -1
    if (a.type === 'start' && b.type === 'end') return 1
    return 0
  })

  // 当前活跃的替换项集合（使用 Set 提高查找效率）
  const activeSet = new Set<number>()

  // 扫描事件点
  for (const event of events) {
    if (event.type === 'start') {
      // 新替换项开始，检查与所有活跃替换项的重叠
      const r1 = replacements[event.index]
      for (const activeIndex of activeSet) {
        const r2 = replacements[activeIndex]
        // 检查重叠（注意：由于排序，r1.start >= r2.start，只需检查 r1.start < r2.end）
        if (r1.start < r2.end) {
          const overlapStart = Math.max(r1.start, r2.start)
          const overlapEnd = Math.min(r1.end, r2.end)
          // 确保有实际重叠（不是边界接触）
          if (overlapStart < overlapEnd) {
            overlaps.push({
              index1: Math.min(event.index, activeIndex),
              index2: Math.max(event.index, activeIndex),
              overlapStart,
              overlapEnd,
              message: `术语 "${r1.originalText}" (位置 ${r1.start}-${r1.end}) 和 "${r2.originalText}" (位置 ${r2.start}-${r2.end}) 在位置 ${overlapStart}-${overlapEnd} 重叠`,
            })
          }
        }
      }
      activeSet.add(event.index)
    } else {
      // 替换项结束，从活跃集合中移除
      activeSet.delete(event.index)
    }
  }

  return overlaps
}

/**
 * 解决术语冲突，根据策略过滤冲突的替换项
 * @param replacements 替换项数组
 * @param strategy 冲突解决策略
 * @returns 过滤后的替换项数组和冲突信息
 */
export function resolveTermConflicts(
  replacements: Array<{
    start: number
    end: number
    placeholder: string
    originalText: string
    targetTerm: string
    termIndex: number
    termId: number
  }>,
  strategy: 'longest' | 'first' | 'skip' = 'longest'
): {
  filteredReplacements: Array<{
    start: number
    end: number
    placeholder: string
    originalText: string
    targetTerm: string
    termIndex: number
    termId: number
  }>
  resolvedConflicts: Array<{
    kept: { originalText: string; start: number; end: number }
    removed: { originalText: string; start: number; end: number }
    reason: string
  }>
} {
  if (replacements.length === 0) {
    return {
      filteredReplacements: [],
      resolvedConflicts: [],
    }
  }

  // 快速路径：如果只有一个替换项，不可能有冲突
  if (replacements.length === 1) {
    return {
      filteredReplacements: replacements,
      resolvedConflicts: [],
    }
  }

  // 检测所有重叠
  const overlaps = detectPositionOverlaps(replacements)

  // 快速路径：如果没有重叠，直接返回
  if (overlaps.length === 0) {
    return {
      filteredReplacements: replacements,
      resolvedConflicts: [],
    }
  }

  // 创建冲突组：每个替换项可能与其他多个替换项冲突
  const conflictGroups = new Map<number, Set<number>>()
  overlaps.forEach((overlap) => {
    if (!conflictGroups.has(overlap.index1)) {
      conflictGroups.set(overlap.index1, new Set())
    }
    if (!conflictGroups.has(overlap.index2)) {
      conflictGroups.set(overlap.index2, new Set())
    }
    conflictGroups.get(overlap.index1)!.add(overlap.index2)
    conflictGroups.get(overlap.index2)!.add(overlap.index1)
  })

  const resolvedConflicts: Array<{
    kept: { originalText: string; start: number; end: number }
    removed: { originalText: string; start: number; end: number }
    reason: string
  }> = []

  const toRemove = new Set<number>()

  // 根据策略解决冲突
  if (strategy === 'longest') {
    // 优先保留更长的术语
    conflictGroups.forEach((conflictSet, index1) => {
      if (toRemove.has(index1)) return

      const r1 = replacements[index1]
      const r1Length = r1.end - r1.start

      conflictSet.forEach((index2) => {
        if (toRemove.has(index2)) return

        const r2 = replacements[index2]
        const r2Length = r2.end - r2.start

        if (r1Length > r2Length) {
          toRemove.add(index2)
          resolvedConflicts.push({
            kept: {
              originalText: r1.originalText,
              start: r1.start,
              end: r1.end,
            },
            removed: {
              originalText: r2.originalText,
              start: r2.start,
              end: r2.end,
            },
            reason: `保留更长的术语 "${r1.originalText}" (长度 ${r1Length})，移除 "${r2.originalText}" (长度 ${r2Length})`,
          })
        } else if (r2Length > r1Length) {
          toRemove.add(index1)
          resolvedConflicts.push({
            kept: {
              originalText: r2.originalText,
              start: r2.start,
              end: r2.end,
            },
            removed: {
              originalText: r1.originalText,
              start: r1.start,
              end: r1.end,
            },
            reason: `保留更长的术语 "${r2.originalText}" (长度 ${r2Length})，移除 "${r1.originalText}" (长度 ${r1Length})`,
          })
        } else {
          // 长度相同，按 termIndex 优先（先出现的优先）
          if (r1.termIndex <= r2.termIndex) {
            toRemove.add(index2)
            resolvedConflicts.push({
              kept: {
                originalText: r1.originalText,
                start: r1.start,
                end: r1.end,
              },
              removed: {
                originalText: r2.originalText,
                start: r2.start,
                end: r2.end,
              },
              reason: `长度相同，保留先出现的术语 "${r1.originalText}" (索引 ${r1.termIndex})，移除 "${r2.originalText}" (索引 ${r2.termIndex})`,
            })
          } else {
            toRemove.add(index1)
            resolvedConflicts.push({
              kept: {
                originalText: r2.originalText,
                start: r2.start,
                end: r2.end,
              },
              removed: {
                originalText: r1.originalText,
                start: r1.start,
                end: r1.end,
              },
              reason: `长度相同，保留先出现的术语 "${r2.originalText}" (索引 ${r2.termIndex})，移除 "${r1.originalText}" (索引 ${r1.termIndex})`,
            })
          }
        }
      })
    })
  } else if (strategy === 'first') {
    // 按 termIndex 优先，先出现的术语优先
    conflictGroups.forEach((conflictSet, index1) => {
      if (toRemove.has(index1)) return

      const r1 = replacements[index1]

      conflictSet.forEach((index2) => {
        if (toRemove.has(index2)) return

        const r2 = replacements[index2]

        if (r1.termIndex < r2.termIndex) {
          toRemove.add(index2)
          resolvedConflicts.push({
            kept: {
              originalText: r1.originalText,
              start: r1.start,
              end: r1.end,
            },
            removed: {
              originalText: r2.originalText,
              start: r2.start,
              end: r2.end,
            },
            reason: `保留先出现的术语 "${r1.originalText}" (索引 ${r1.termIndex})，移除 "${r2.originalText}" (索引 ${r2.termIndex})`,
          })
        } else if (r2.termIndex < r1.termIndex) {
          toRemove.add(index1)
          resolvedConflicts.push({
            kept: {
              originalText: r2.originalText,
              start: r2.start,
              end: r2.end,
            },
            removed: {
              originalText: r1.originalText,
              start: r1.start,
              end: r1.end,
            },
            reason: `保留先出现的术语 "${r2.originalText}" (索引 ${r2.termIndex})，移除 "${r1.originalText}" (索引 ${r1.termIndex})`,
          })
        } else {
          // termIndex 相同，按位置优先（先匹配的优先）
          if (r1.start <= r2.start) {
            toRemove.add(index2)
            resolvedConflicts.push({
              kept: {
                originalText: r1.originalText,
                start: r1.start,
                end: r1.end,
              },
              removed: {
                originalText: r2.originalText,
                start: r2.start,
                end: r2.end,
              },
              reason: `保留先匹配的术语 "${r1.originalText}" (位置 ${r1.start})，移除 "${r2.originalText}" (位置 ${r2.start})`,
            })
          } else {
            toRemove.add(index1)
            resolvedConflicts.push({
              kept: {
                originalText: r2.originalText,
                start: r2.start,
                end: r2.end,
              },
              removed: {
                originalText: r1.originalText,
                start: r1.start,
                end: r1.end,
              },
              reason: `保留先匹配的术语 "${r2.originalText}" (位置 ${r2.start})，移除 "${r1.originalText}" (位置 ${r1.start})`,
            })
          }
        }
      })
    })
  } else if (strategy === 'skip') {
    // 跳过所有冲突的术语
    conflictGroups.forEach((conflictSet, index) => {
      toRemove.add(index)
      conflictSet.forEach((otherIndex) => {
        toRemove.add(otherIndex)
      })
    })

    // 记录所有被跳过的冲突
    conflictGroups.forEach((conflictSet, index1) => {
      const r1 = replacements[index1]
      conflictSet.forEach((index2) => {
        const r2 = replacements[index2]
        resolvedConflicts.push({
          kept: {
            originalText: '',
            start: -1,
            end: -1,
          },
          removed: {
            originalText: r1.originalText,
            start: r1.start,
            end: r1.end,
          },
          reason: `跳过冲突的术语 "${r1.originalText}" 和 "${r2.originalText}"`,
        })
      })
    })
  }

  // 过滤掉需要移除的替换项
  const filteredReplacements = replacements.filter((_, index) => !toRemove.has(index))

  return {
    filteredReplacements,
    resolvedConflicts,
  }
}
